import React, {useState, useEffect, useContext, useRef} from "react";
import qs from 'query-string'

import * as Yup from "yup";
import {useHistory} from "react-router-dom";
import {Link as RouterLink} from "react-router-dom";
import {toast} from "react-toastify";
import {Formik, Form, Field} from "formik";

import CssBaseline from "@material-ui/core/CssBaseline";
import Link from "@material-ui/core/Link";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import usePlans from '../../hooks/usePlans';
import {i18n} from "../../translate/i18n";
import {Helmet} from "react-helmet";

import {openApi} from "../../services/api";
import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import ColorModeContext from "../../layout/themeContext";
import {getBackendUrl} from "../../config";
import clsx from "clsx";

const useStyles = makeStyles(theme => {
    const {mode} = theme;
    return ({
        root: {
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            padding: "0",
            boxSizing: "border-box",
            overflow: "auto",
            position: "relative",
            backgroundColor: theme.palette.background ? theme.palette.background.default : (theme.palette.type === "light" ? "#f5f5f5" : "#303030"),
            backgroundImage: "none",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center center",
            [theme.breakpoints.down("sm")]: {
                flexDirection: "column",
            },
        },
        containerSignup: {
            padding: "16px",
            width: "80%",
            maxWidth: "900px",
            boxSizing: "border-box",
            zIndex: 10,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            position: "fixed",
            margin: "0 auto",
            [theme.breakpoints.down("md")]: {
                width: "90%",
            },
            [theme.breakpoints.down("sm")]: {
                width: "95%",
            },
        },
        paper: {
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: `
        0 25px 50px rgba(0, 0, 0, 0.15),
        0 10px 25px rgba(0, 0, 0, 0.1),
        0 1px 0 rgba(255, 255, 255, 0.2) inset,
        0 0 0 1px rgba(255, 255, 255, 0.1)
      `,
            display: "flex",
            flexDirection: "row",
            padding: "40px",
            borderRadius: "20px",
            width: "80%",
            boxSizing: "border-box",
            margin: "0 auto",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            gap: "40px",
            minHeight: "550px",
            [theme.breakpoints.down("md")]: {
                flexDirection: "column",
                padding: "30px",
                gap: "30px",
                minHeight: "auto",
                width: "90%",
            },
            [theme.breakpoints.down("sm")]: {
                padding: "20px",
                gap: "20px",
                minHeight: "auto",
                width: "95%",
            },
        },
        sidebar: {
            flex: "0 0 280px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            [theme.breakpoints.down("md")]: {
                flex: "1",
                flexDirection: "row",
                gap: "10px",
            },
        },
        stepItem: {
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            padding: "12px",
            borderRadius: "12px",
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.05)",
            },
            "&.active": {
                backgroundColor: "rgba(25, 118, 210, 0.1)",
            },
        },
        stepNumber: {
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: "14px",
            backgroundColor: "#e0e0e0",
            color: "#666",
            flexShrink: 0,
            transition: "all 0.3s ease",
            "&.completed": {
                backgroundColor: "#4caf50",
                color: "#fff",
            },
            "&.active": {
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
            },
        },
        stepLabel: {
            display: "flex",
            flexDirection: "column",
            gap: "4px",
        },
        stepTitle: {
            fontWeight: 600,
            fontSize: "14px",
            color: "#333",
        },
        stepDescription: {
            fontSize: "12px",
            color: "#999",
        },
        content: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "20px",
        },
        contentHeader: {
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "20px",
        },
        contentTitle: {
            fontSize: "24px",
            fontWeight: 700,
            color: "#333",
        },
        contentDescription: {
            fontSize: "14px",
            color: "#666",
        },
        progressBar: {
            height: "4px",
            backgroundColor: "#e0e0e0",
            borderRadius: "2px",
            overflow: "hidden",
            marginBottom: "20px",
        },
        progressFill: {
            height: "100%",
            backgroundColor: theme.palette.primary.main,
            transition: "width 0.3s ease",
        },
        formGrid: {
            display: "flex",
            flexDirection: "column",
            gap: "16px",
        },
        formField: {
            display: "flex",
            flexDirection: "column",
            gap: "8px",
        },
        formLabel: {
            fontSize: "14px",
            fontWeight: 500,
            color: "#333",
        },
        formInput: {
            padding: "10px 12px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            fontSize: "14px",
            fontFamily: "inherit",
            transition: "all 0.3s ease",
            "&:focus": {
                outline: "none",
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
            },
        },
        formError: {
            fontSize: "12px",
            color: "#d32f2f",
        },
        buttonContainer: {
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            marginTop: "20px",
        },
        button: {
            padding: "10px 24px",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "14px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s ease",
        },
        buttonPrimary: {
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            "&:hover": {
                backgroundColor: theme.palette.primary.dark,
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            },
        },
        buttonSecondary: {
            backgroundColor: "#f0f0f0",
            color: "#333",
            "&:hover": {
                backgroundColor: "#e0e0e0",
            },
        },
        "@keyframes slideInUp": {
            from: {
                opacity: 0,
                transform: "translateY(20px)",
            },
            to: {
                opacity: 1,
                transform: "translateY(0)",
            },
        },
    });
});

const UserSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    companyName: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    document: Yup.string()
        .test('document-format', 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', function (value) {
            if (!value) return false;
            const numbers = value.replace(/\D/g, '');
            return numbers.length === 11 || numbers.length === 14;
        })
        .required("CPF ou CNPJ é obrigatório"),
    password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
    email: Yup.string().email("Invalid email").required("Required"),
    phone: Yup.string().required("Required"),
});

const SignUp = () => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const {colorMode} = useContext(ColorModeContext);
    const {appLogoFavicon, appName, mode} = colorMode;
    const {getPlanList} = usePlans()
    const [plans, setPlans] = useState([])
    const [loading, setLoading] = useState(false);
    const [validatingCnpj, setValidatingCnpj] = useState(false);
    const [lastValidatedDocument, setLastValidatedDocument] = useState("");
    const {getPublicSetting} = useSettings();
    const [currentStep, setCurrentStep] = useState(0);

    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    const steps = [
        { title: "Empresa", description: "Dados da empresa" },
        { title: "Usuário", description: "Seus dados" },
        { title: "Plano", description: "Escolha seu plano" },
    ];

    let companyId = null
    const params = qs.parse(window.location.search)
    if (params.companyId !== undefined) {
        companyId = params.companyId
    }

    const initialState = {name: "", email: "", password: "", phone: "", companyId, companyName: "", document: "", planId: ""};

    const [user] = useState(initialState);

    useEffect(() => {
        getPublicSetting("userCreation")
            .then((data) => {
                if (data === "disabled") {
                    toast.error(i18n.t("signup.toasts.disabled"));
                    history.push("/login");
                }
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgLight;
                    console.log("Background light URL:", fullUrl);
                    setBackgroundLight(fullUrl);
                } else {
                    setBackgroundLight("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo clara:", err);
                setBackgroundLight("");
            });

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgDark;
                    console.log("Background dark URL:", fullUrl);
                    setBackgroundDark(fullUrl);
                } else {
                    setBackgroundDark("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo escura:", err);
                setBackgroundDark("");
            });
    }, [history, companyId]);

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            try {
                const planList = await getPlanList({listPublic: "false"});
                setPlans(planList || []);
            } catch (err) {
                console.error("Erro ao buscar planos:", err);
                setPlans([]);
            }
            setLoading(false);
        }
        fetchData();
    }, []);

    const validateCpf = (cpf) => {
        const cleanCpf = cpf.replace(/\D/g, '');

        if (cleanCpf.length !== 11) return false;

        if (/^(\d)\1{10}$/.test(cleanCpf)) return false;

        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
        }
        let remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cleanCpf.charAt(9))) return false;

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
        }
        remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cleanCpf.charAt(10))) return false;

        return true;
    };

    const formatCpf = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 11);

        if (limitedNumbers.length <= 3) return limitedNumbers;
        if (limitedNumbers.length <= 6) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3)}`;
        if (limitedNumbers.length <= 9) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6)}`;
        return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6, 9)}-${limitedNumbers.slice(9)}`;
    };

    const formatCnpj = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 14);

        if (limitedNumbers.length <= 2) return limitedNumbers;
        if (limitedNumbers.length <= 5) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2)}`;
        if (limitedNumbers.length <= 8) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5)}`;
        if (limitedNumbers.length <= 12) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8)}`;
        return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8, 12)}-${limitedNumbers.slice(12)}`;
    };

    const detectDocumentType = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) return 'cpf';
        return 'cnpj';
    };

    const formatDocument = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return formatCpf(value);
        } else {
            return formatCnpj(value);
        }
    };

    const validateDocument = async (document, setFieldValue) => {
        const cleanDocument = document.replace(/\D/g, '');

        if (lastValidatedDocument === cleanDocument) {
            return true;
        }

        const documentType = detectDocumentType(document);

        if (documentType === 'cpf') {
            if (validateCpf(document)) {
                setLastValidatedDocument(cleanDocument);
                toast.success("CPF válido!");
                return true;
            } else {
                setLastValidatedDocument(cleanDocument);
                toast.error("CPF inválido!");
                return false;
            }
        } else {
            if (cleanDocument.length !== 14) return false;

            setValidatingCnpj(true);
            try {
                const response = await openApi.post("/auth/validate-cnpj", {cnpj: cleanDocument});
                const data = response.data;
                setValidatingCnpj(false);

                if (data.valid && data.data.nome) {
                    if (data.data.tipo === 'cnpj') {
                        setFieldValue('companyName', data.data.nome);
                        toast.success("CNPJ válido! Nome da empresa preenchido automaticamente.");
                    } else {
                        toast.success("CPF válido!");
                    }
                    setLastValidatedDocument(cleanDocument);
                    return true;
                } else {
                    setLastValidatedDocument(cleanDocument);
                    toast.error("Documento inválido ou não encontrado na Receita Federal");
                    return false;
                }
            } catch (error) {
                setValidatingCnpj(false);
                console.error("Erro ao validar documento:", error);
                toast.error("Erro ao validar documento. Tente novamente.");
                setLastValidatedDocument(cleanDocument);
                return false;
            }
        }
    };

    const handleSignUp = async values => {
        try {
            await openApi.post("/auth/signup", values);
            toast.success(i18n.t("signup.toasts.success"));
            history.push("/login");
        } catch (err) {
            toastError(err);
        }
    };

    const validateStep = (stepIndex, values, errors) => {
        switch (stepIndex) {
            case 0:
                return !errors.document && !errors.companyName && values.document && values.companyName;
            case 1:
                return !errors.name && !errors.email && !errors.password && !errors.phone && 
                       values.name && values.email && values.password && values.phone;
            case 2:
                return !errors.planId && values.planId;
            default:
                return false;
        }
    };

    let finalBackground;

    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';

    if (mode === "light") {
        if (hasValidLightBg) {
            finalBackground = `url(${backgroundLight})`;
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#f5f5f5";
        }
    } else {
        if (hasValidDarkBg) {
            finalBackground = `url(${backgroundDark})`;
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#303030";
        }
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{appName || "Multi100 - Cadastro"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"}/>
            </Helmet>

            <div
                className={classes.root}
                style={{
                    backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(")
                        ? finalBackground
                        : "none"
                }}
            >
                <Container
                    component="main"
                    maxWidth={false}
                    className={classes.containerSignup}
                >
                    <CssBaseline/>
                    <div className={classes.paper}>
                        {/* Sidebar com Steps */}
                        <div className={classes.sidebar}>
                            {steps.map((step, index) => (
                                <div
                                    key={index}
                                    className={`${classes.stepItem} ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                                    onClick={() => index <= currentStep && setCurrentStep(index)}
                                >
                                    <div className={`${classes.stepNumber} ${index < currentStep ? 'completed' : ''} ${index === currentStep ? 'active' : ''}`}>
                                        {index < currentStep ? '✓' : index + 1}
                                    </div>
                                    <div className={classes.stepLabel}>
                                        <div className={classes.stepTitle}>{step.title}</div>
                                        <div className={classes.stepDescription}>{step.description}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Conteúdo Principal */}
                        <div className={classes.content}>
                            {/* Header */}
                            <div className={classes.contentHeader}>
                                <div className={classes.contentTitle}>{steps[currentStep].title}</div>
                                <div className={classes.contentDescription}>{steps[currentStep].description}</div>
                            </div>

                            {/* Progress Bar */}
                            <div className={classes.progressBar}>
                                <div 
                                    className={classes.progressFill}
                                    style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                                />
                            </div>

                            {/* Formulário */}
                            <Formik
                                initialValues={user}
                                enableReinitialize={true}
                                validationSchema={UserSchema}
                                onSubmit={(values, actions) => {
                                    setTimeout(() => {
                                        handleSignUp(values);
                                        actions.setSubmitting(false);
                                    }, 100); // Reduzir delay para melhor UX
                                }}
                            >
                                {({touched, errors, isSubmitting, setFieldValue, values}) => (
                                    <Form className={classes.formGrid}>
                                        {/* Step 0: Empresa */}
                                        {currentStep === 0 && (
                                            <>
                                                <div className={classes.formField}>
                                                    <label className={classes.formLabel}>CPF ou CNPJ</label>
                                                    <Field
                                                        as="input"
                                                        type="text"
                                                        className={classes.formInput}
                                                        id="document"
                                                        name="document"
                                                        placeholder="Digite CPF ou CNPJ"
                                                        maxLength="18"
                                                        onChange={(e) => {
                                                            let value = e.target.value;
                                                            const numbers = value.replace(/\D/g, '');
                                                            if (lastValidatedDocument && lastValidatedDocument !== numbers) {
                                                                setLastValidatedDocument("");
                                                            }
                                                            const formattedValue = formatDocument(value);
                                                            setFieldValue('document', formattedValue);
                                                            if (numbers.length === 11 || numbers.length === 14) {
                                                                validateDocument(formattedValue, setFieldValue);
                                                            }
                                                        }}
                                                    />
                                                    {touched.document && errors.document && (
                                                        <div className={classes.formError}>{errors.document}</div>
                                                    )}
                                                </div>

                                                <div className={classes.formField}>
                                                    <label className={classes.formLabel}>Nome da Empresa</label>
                                                    <Field
                                                        as="input"
                                                        type="text"
                                                        className={classes.formInput}
                                                        id="companyName"
                                                        name="companyName"
                                                        placeholder="Digite o nome da empresa"
                                                    />
                                                    {touched.companyName && errors.companyName && (
                                                        <div className={classes.formError}>{errors.companyName}</div>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* Step 1: Usuário */}
                                        {currentStep === 1 && (
                                            <>
                                                <div className={classes.formField}>
                                                    <label className={classes.formLabel}>Nome Completo</label>
                                                    <Field
                                                        as="input"
                                                        type="text"
                                                        className={classes.formInput}
                                                        id="name"
                                                        name="name"
                                                        placeholder="Digite seu nome completo"
                                                    />
                                                    {touched.name && errors.name && (
                                                        <div className={classes.formError}>{errors.name}</div>
                                                    )}
                                                </div>

                                                <div className={classes.formField}>
                                                    <label className={classes.formLabel}>Email</label>
                                                    <Field
                                                        as="input"
                                                        type="email"
                                                        className={classes.formInput}
                                                        id="email"
                                                        name="email"
                                                        placeholder="Digite seu email"
                                                    />
                                                    {touched.email && errors.email && (
                                                        <div className={classes.formError}>{errors.email}</div>
                                                    )}
                                                </div>

                                                <div className={classes.formField}>
                                                    <label className={classes.formLabel}>Senha</label>
                                                    <Field
                                                        as="input"
                                                        type="password"
                                                        className={classes.formInput}
                                                        id="password"
                                                        name="password"
                                                        placeholder="Digite uma senha segura"
                                                    />
                                                    {touched.password && errors.password && (
                                                        <div className={classes.formError}>{errors.password}</div>
                                                    )}
                                                </div>

                                                <div className={classes.formField}>
                                                    <label className={classes.formLabel}>Telefone</label>
                                                    <Field
                                                        as="input"
                                                        type="tel"
                                                        className={classes.formInput}
                                                        id="phone"
                                                        name="phone"
                                                        placeholder="Digite seu telefone"
                                                    />
                                                    {touched.phone && errors.phone && (
                                                        <div className={classes.formError}>{errors.phone}</div>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* Step 2: Plano */}
                                        {currentStep === 2 && (
                                            <div className={classes.formField}>
                                                <label className={classes.formLabel}>Selecione um Plano</label>
                                                <Field
                                                    as="select"
                                                    className={classes.formInput}
                                                    id="plan-selection"
                                                    name="planId"
                                                >
                                                    <option value="">Escolha um plano...</option>
                                                    {plans.map((plan) => (
                                                        <option key={plan.id} value={plan.id}>
                                                            {plan.name} - Atendentes: {plan.users} - WhatsApp: {plan.connections} - Filas: {plan.queues} - R$ {plan.amount}
                                                        </option>
                                                    ))}
                                                </Field>
                                                {touched.planId && errors.planId && (
                                                    <div className={classes.formError}>{errors.planId}</div>
                                                )}
                                            </div>
                                        )}

                                        {/* Botões de Navegação */}
                                        <div className={classes.buttonContainer}>
                                            {currentStep > 0 && (
                                                <button
                                                    type="button"
                                                    className={`${classes.button} ${classes.buttonSecondary}`}
                                                    onClick={() => setCurrentStep(currentStep - 1)}
                                                >
                                                    Voltar
                                                </button>
                                            )}
                                            {currentStep < steps.length - 1 ? (
                                                <button
                                                    type="button"
                                                    className={`${classes.button} ${classes.buttonPrimary}`}
                                                    onClick={() => {
                                                        if (validateStep(currentStep, values, errors)) {
                                                            setCurrentStep(currentStep + 1);
                                                        } else {
                                                            toast.error("Por favor, preencha todos os campos obrigatórios");
                                                        }
                                                    }}
                                                >
                                                    Próximo
                                                </button>
                                            ) : (
                                                <button
                                                    type="submit"
                                                    className={`${classes.button} ${classes.buttonPrimary}`}
                                                    disabled={isSubmitting || validatingCnpj}
                                                >
                                                    {isSubmitting ? 'Criando conta...' : 'Criar Conta'}
                                                </button>
                                            )}
                                        </div>

                                        {/* Link para Login */}
                                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                            <span style={{ color: '#666', fontSize: '14px' }}>
                                                Já tem uma conta?{' '}
                                                <Link
                                                    component={RouterLink}
                                                    to="/login"
                                                    style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}
                                                >
                                                    Faça login
                                                </Link>
                                            </span>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default SignUp;